import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Wallet,
  Phone,
  ArrowRight,
  ShieldCheck,
  Clock,
  HelpCircle,
  FileCheck,
  ChevronRight,
} from "lucide-react";
import SEO from "../components/SEO";
import { COMPANY } from "../lib/constants";

const PAYMENT_FAQS = [
  {
    q: "Do you accept Virginia Medicaid?",
    a: "Yes — 1st Home Care Services is an approved Virginia Medicaid provider. Our team will help verify your benefits and start care once eligibility is confirmed.",
  },
  {
    q: "What is private pay?",
    a: "Private pay means you (or a family member) pay for services directly, without going through insurance. We offer flexible scheduling and transparent rates so you control how much support you receive.",
  },
  {
    q: "Do you accept long-term care insurance?",
    a: "Many long-term care insurance policies cover services like ours. Bring us your policy and our team will help review the benefits and coordinate the claim.",
  },
  {
    q: "Is a free assessment available?",
    a: "Yes. We offer a complimentary in-home assessment so we can build a personalized plan and confirm what's covered before any commitment.",
  },
];

export default function Payment() {
  return (
    <>
      <SEO
        title="Payment Options | Medicaid & Private Pay | 1st Home Care Services"
        description="Flexible payment options for home care in Northern Virginia. We accept Virginia Medicaid and private pay. Free in-home assessment available."
        keywords="Medicaid home care Virginia, private pay home care VA, home care payment options Springfield, long-term care insurance Virginia"
        canonical="https://1sthomecare.com/payment"
      />

      {/* HERO */}
      <section id="payment-section" className="container-pad section-pad">
        <div className="max-w-3xl">
          <nav
            data-testid="payment-breadcrumb"
            aria-label="Breadcrumb"
            className="flex items-center gap-2 text-sm text-gray-500 mb-4"
          >
            <Link to="/contact" className="hover:text-brand-blue transition-colors">Contact</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-brand-blue-dark font-semibold">Payment</span>
          </nav>
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Payment Options
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Flexible payment for every family.
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            We accept Virginia Medicaid and offer private pay options — so
            families across Northern Virginia can access the care they need,
            without the paperwork stress. Our team will walk you through every
            option and help start care quickly.
          </p>
        </div>
      </section>

      {/* MEDICAID + PRIVATE PAY CARDS */}
      <section className="container-pad pb-16 md:pb-24">
        <div className="grid md:grid-cols-2 gap-6">
          <article
            data-testid="payment-card-medicaid"
            className="relative bg-white rounded-3xl border border-gray-100 shadow-md p-8 md:p-10 overflow-hidden"
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand-blue/5" aria-hidden />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-brand-blue text-white flex items-center justify-center mb-5">
                <BadgeCheck className="w-7 h-7" />
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-brand-blue mb-2">
                Insurance
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-brand-blue-dark">
                Medicaid Accepted
              </h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                We're an approved Virginia Medicaid provider. If your loved one
                qualifies, Medicaid can cover most or all of the cost of home care.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Personal Care via VA Medicaid",
                  "Private Duty Nursing (when authorized)",
                  "Skilled Nursing services",
                  "Eligibility verification by our team",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <BadgeCheck className="w-4 h-4 text-brand-amber mt-0.5 shrink-0" />
                    <span className="text-gray-700">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>

          <article
            data-testid="payment-card-private"
            className="relative bg-brand-blue-dark text-white rounded-3xl shadow-md p-8 md:p-10 overflow-hidden"
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-brand-amber/20" aria-hidden />
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-brand-amber text-white flex items-center justify-center mb-5">
                <Wallet className="w-7 h-7" />
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-brand-amber-light mb-2">
                Out-of-Pocket
              </div>
              <h2 className="font-heading text-2xl md:text-3xl font-semibold">
                Private Pay Available
              </h2>
              <p className="mt-3 text-white/85 leading-relaxed">
                Want full flexibility on schedule, hours, and caregiver? Private pay
                gives you complete control — with transparent pricing and no insurance hurdles.
              </p>
              <ul className="mt-5 space-y-2 text-sm">
                {[
                  "Hourly, overnight, weekend, or live-in",
                  "Choose your own schedule — no minimums",
                  "Transparent rates, no hidden fees",
                  "Long-term care insurance accepted",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <BadgeCheck className="w-4 h-4 text-brand-amber-light mt-0.5 shrink-0" />
                    <span className="text-white/90">{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section data-testid="payment-process" className="bg-brand-gray/50 section-pad">
        <div className="container-pad">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              How It Works
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
              From inquiry to care — in 3 simple steps
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Phone, title: "Free Phone Consultation", desc: "Tell us about your needs. We'll explain Medicaid eligibility and pricing." },
              { icon: FileCheck, title: "In-Home Assessment", desc: "Our RN visits to build a personalized care plan and confirm coverage." },
              { icon: Clock, title: "Care Starts in 24–48 Hours", desc: "Once approved, your caregiver is matched and care begins right away." },
            ].map((s, i) => (
              <div
                key={s.title}
                data-testid={`process-step-${i + 1}`}
                className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-all relative"
              >
                <div className="absolute -top-3 -left-3 w-10 h-10 rounded-full bg-brand-blue text-white text-sm font-bold flex items-center justify-center shadow-md">
                  {i + 1}
                </div>
                <div className="w-12 h-12 rounded-xl bg-brand-amber/10 text-brand-amber-dark flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="font-heading text-lg font-semibold text-brand-blue-dark">{s.title}</div>
                <div className="text-sm text-gray-600 mt-2">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST & FAQ */}
      <section className="container-pad section-pad">
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
              Why It's Easy
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-blue-dark tracking-tight">
              We handle the paperwork. You focus on family.
            </h2>
            <ul className="mt-6 space-y-4">
              {[
                { icon: ShieldCheck, title: "Approved Virginia Provider", desc: "Licensed home care organization, fully credentialed." },
                { icon: BadgeCheck, title: "Free Eligibility Check", desc: "We verify your Medicaid benefits and explain coverage." },
                { icon: Clock, title: "Fast Start", desc: "Most families begin care within 24–48 hours of intake." },
                { icon: HelpCircle, title: "No Pressure", desc: "Our in-home assessment is free — no commitment required." },
              ].map((t) => (
                <li key={t.title} className="flex gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand-blue/10 text-brand-blue flex items-center justify-center shrink-0">
                    <t.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-heading font-semibold text-brand-blue-dark">{t.title}</div>
                    <div className="text-sm text-gray-600 mt-0.5">{t.desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-2xl font-semibold text-brand-blue-dark mb-5">
              Payment FAQs
            </h3>
            <div className="space-y-3">
              {PAYMENT_FAQS.map((f, i) => (
                <details
                  key={f.q}
                  data-testid={`payment-faq-${i}`}
                  className="group bg-white border border-gray-100 rounded-2xl p-5 shadow-sm open:shadow-md transition-all"
                >
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-3 font-heading font-semibold text-brand-blue-dark">
                    <span>{f.q}</span>
                    <span className="text-brand-amber group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                  </summary>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-blue-dark text-white section-pad relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 grain-overlay" aria-hidden />
        <div className="container-pad relative text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight max-w-2xl mx-auto">
            Not sure which option is right for your family?
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">
            Call us — a care coordinator will walk you through coverage, costs, and next steps.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="payment-call-now"
              className="inline-flex items-center gap-2 bg-brand-amber hover:bg-brand-amber-dark text-white font-semibold px-7 py-4 rounded-full shadow-md"
            >
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="payment-contact"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-7 py-4 rounded-full backdrop-blur"
            >
              Request Care Online <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
